import { CheckIcon } from "@xola/icons";
import { almostZero, Button, Modal, Table } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { Purchase, PurchaseItem } from "types";
import { Currency } from "@/components/Common/Currency";
import { PurchaseDrawer } from "@/components/Dashboard/Roster/RosterGuestList/PurchaseDrawer";
import { usePurchase } from "@/hooks/resources/usePurchase";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";
import { InteracPurchaseDetails } from "@/stores/interacPurchaseRefundStore";

interface InteracRefundWarningModalProps {
    isOpen: boolean;
    interacPurchaseDetails: InteracPurchaseDetails[] | null;
    onClose: () => void;
}

export const InteracRefundWarningModal = ({
    isOpen,
    onClose,
    interacPurchaseDetails,
}: InteracRefundWarningModalProps) => {
    if (interacPurchaseDetails === null) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <Modal.Header description="The following purchases could not be fully refunded because some payments were made using Interac cards. For Interac refunds, the physical card must be presented.">
                Pending Interac Refunds
            </Modal.Header>

            <Modal.Body>
                <div className="space-y-4">
                    <InteracPurchaseTable interacPurchaseDetails={interacPurchaseDetails} />
                </div>
            </Modal.Body>

            <Modal.Footer className="space-x-2">
                <Button color="primary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const InteracPurchaseTable = ({
    interacPurchaseDetails,
}: {
    interacPurchaseDetails: InteracPurchaseDetails[];
}) => {
    return (
        <div className="space-y-2">
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Name</Table.Header>
                        <Table.Header>Balance</Table.Header>
                        <Table.Header />
                    </Table.Row>
                </Table.Head>

                <Table.Body>
                    {interacPurchaseDetails.map((purchaseDetail: InteracPurchaseDetails) => {
                        return <PurchaseRow key={purchaseDetail.purchaseId} purchaseDetail={purchaseDetail} />;
                    })}
                </Table.Body>
            </Table>
        </div>
    );
};

export const PurchaseRow = ({ purchaseDetail, key }: { purchaseDetail: InteracPurchaseDetails; key: ID }) => {
    const [isPurchaseDrawerVisible, { toggle: togglePurchaseDrawer }] = useVisibility(false);
    const [selectedDrawerPurchase, setSelectedDrawerPurchase] = useState<Purchase | null>(null);
    const [selectedDrawerItem, setSelectedDrawerItem] = useState<PurchaseItem | null>(null);

    const { data: purchase } = usePurchase(purchaseDetail.purchaseId);
    const { data: item } = useResource<PurchaseItem>(["purchaseItems", purchaseDetail.itemId]);

    useEffect(() => {
        if (purchase && item) {
            setSelectedDrawerPurchase(purchase);
            setSelectedDrawerItem(item);
        }
    }, [purchase, item]);

    if (!purchase) {
        return <>No purchase found.</>;
    }

    const handleTogglePurchaseDrawer = () => {
        const drawerPurchaseItem = item ?? purchase.items[0];
        setSelectedDrawerItem(drawerPurchaseItem);
        setSelectedDrawerPurchase(purchase);
        togglePurchaseDrawer();
    };

    const handleClosePurchaseDrawer = () => {
        setSelectedDrawerItem(null);
        setSelectedDrawerPurchase(null);
        togglePurchaseDrawer();
    };

    const balance = Math.abs(purchase.balance);
    return (
        <>
            <Table.Row key={key} className="justify-between">
                <Table.Cell
                    className="no-translate w-1/2 cursor-pointer text-blue underline"
                    onClick={handleTogglePurchaseDrawer}
                >
                    {purchase.customerName}
                </Table.Cell>
                <Table.Cell className="no-translate w-1/2">
                    <Currency>{balance}</Currency>
                    {almostZero(balance) && <CheckIcon className=" ml-1 text-green" />}
                </Table.Cell>
            </Table.Row>

            <PurchaseDrawer
                selectedPurchase={selectedDrawerPurchase}
                selectedItem={selectedDrawerItem}
                isOpen={isPurchaseDrawerVisible}
                onClose={handleClosePurchaseDrawer}
            />
        </>
    );
};
